import {
  Icon
} from "./chunk-7IEFGXFA.js";
import {
  cn
} from "./chunk-SBEJSA5Q.js";

// src/components/canvas/lightbox/VideoPlayer.tsx
import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
var FRAME_SECONDS = 1 / 30;
var SEEK_SECONDS = 5;
function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor(total % 3600 / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}
function VideoPlayer({ src, filename, isOpen, onClose, onDownload }) {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const [dims, setDims] = useState(null);
  const [hoverTime, setHoverTime] = useState(null);
  const video = () => videoRef.current;
  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setControlsVisible(false);
    }, 2500);
  }, []);
  const togglePlay = useCallback(() => {
    const v = video();
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
    showControls();
  }, [showControls]);
  const seekBy = useCallback((delta) => {
    const v = video();
    if (!v) return;
    v.currentTime = Math.min(Math.max(0, v.currentTime + delta), v.duration || 0);
    showControls();
  }, [showControls]);
  const stepFrame = useCallback((direction) => {
    const v = video();
    if (!v) return;
    v.pause();
    seekBy(direction * FRAME_SECONDS);
  }, [seekBy]);
  const toggleMute = useCallback(() => {
    const v = video();
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    showControls();
  }, [showControls]);
  const toggleLoop = useCallback(() => {
    const v = video();
    if (!v) return;
    v.loop = !v.loop;
    setLoop(v.loop);
    showControls();
  }, [showControls]);
  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen?.();
    showControls();
  }, [showControls]);
  const togglePiP = useCallback(() => {
    const v = video();
    if (!v || !document.pictureInPictureEnabled) return;
    if (document.pictureInPictureElement) void document.exitPictureInPicture();
    else void v.requestPictureInPicture?.();
  }, []);
  const applySpeed = useCallback((rate) => {
    const v = video();
    if (!v) return;
    v.playbackRate = rate;
    setSpeed(rate);
    setSpeedMenuOpen(false);
    showControls();
  }, [showControls]);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      const target = e.target;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      switch (e.key) {
        case "Escape":
          if (document.fullscreenElement) void document.exitFullscreen();
          else onClose();
          break;
        case " ":
        case "k":
        case "K":
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          e.preventDefault();
          seekBy(-SEEK_SECONDS);
          break;
        case "ArrowRight":
          e.preventDefault();
          seekBy(SEEK_SECONDS);
          break;
        case ",":
          stepFrame(-1);
          break;
        case ".":
          stepFrame(1);
          break;
        case "m":
        case "M":
          toggleMute();
          break;
        case "l":
        case "L":
          toggleLoop();
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
        case "p":
        case "P":
          togglePiP();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, togglePlay, seekBy, stepFrame, toggleMute, toggleLoop, toggleFullscreen, togglePiP]);
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  useEffect(() => {
    if (!isOpen) return;
    setPlaying(false);
    setTime(0);
    setDuration(0);
    setBuffered(0);
    setDims(null);
    setSpeedMenuOpen(false);
    setControlsVisible(true);
  }, [isOpen, src]);
  useEffect(() => () => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
  }, []);
  if (!isOpen) return null;
  const progress = duration > 0 ? time / duration * 100 : 0;
  const bufferedPct = duration > 0 ? buffered / duration * 100 : 0;
  const onScrub = (e) => {
    const v = video();
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max(0, (e.clientX - rect.left) / rect.width), 1);
    v.currentTime = ratio * duration;
    showControls();
  };
  const onScrubHover = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverTime(Math.min(Math.max(0, (e.clientX - rect.left) / rect.width), 1) * duration);
  };
  const iconBtn = "flex h-9 w-9 items-center justify-center rounded-md text-white/90 hover:bg-white/15 transition-colors disabled:opacity-40";
  return /* @__PURE__ */ jsx(Dialog.Root, { open: isOpen, onOpenChange: (open) => {
    if (!open) onClose();
  }, children: /* @__PURE__ */ jsx(Dialog.Portal, { children: /* @__PURE__ */ jsx(Dialog.Content, { asChild: true, "aria-describedby": void 0, children: /* @__PURE__ */ jsxs(
    "div",
    {
      className: "fixed inset-0 z-[9999] flex items-center justify-center bg-black/95",
      onClick: onClose,
      onMouseDown: (e) => e.stopPropagation(),
      onMouseMove: (e) => e.stopPropagation(),
      onMouseUp: (e) => e.stopPropagation(),
      onWheel: (e) => e.stopPropagation(),
      role: "dialog",
      "aria-modal": "true",
      "aria-label": `Preview ${filename}`,
      children: [
        /* @__PURE__ */ jsx(Dialog.Title, { className: "sr-only", children: filename }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            ref: wrapperRef,
            className: cn(
              "relative flex flex-col bg-black select-none",
              fullscreen ? "h-screen w-screen" : "max-h-[88vh] max-w-[92vw] rounded-xl overflow-hidden shadow-2xl"
            ),
            onClick: (e) => e.stopPropagation(),
            onMouseMove: showControls,
            onMouseLeave: () => {
              if (videoRef.current && !videoRef.current.paused) setControlsVisible(false);
            },
            children: [
              /* @__PURE__ */ jsxs("div", { className: cn("absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 bg-gradient-to-b from-black/80 to-transparent px-4 py-3 transition-opacity", !controlsVisible && "opacity-0 pointer-events-none"), children: [
                /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
                  /* @__PURE__ */ jsx("div", { className: "truncate text-sm font-medium text-white", children: filename }),
                  /* @__PURE__ */ jsxs("div", { className: "text-xs text-white/60", children: [
                    dims ? `${dims.w}\xD7${dims.h}` : "\u2014",
                    duration ? ` \xB7 ${formatTime(duration)}` : ""
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                  onDownload && /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Download", onClick: onDownload, children: /* @__PURE__ */ jsx(Icon, { name: "download", className: "h-4 w-4" }) }),
                  /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Close (Esc)", onClick: onClose, "aria-label": "Close", children: /* @__PURE__ */ jsx(Icon, { name: "x", className: "h-5 w-5" }) })
                ] })
              ] }),
              /* @__PURE__ */ jsx(
                "video",
                {
                  ref: videoRef,
                  src,
                  className: cn("block bg-black", fullscreen ? "h-full w-full object-contain" : "max-h-[88vh] max-w-[92vw] object-contain"),
                  playsInline: true,
                  onClick: togglePlay,
                  onDoubleClick: toggleFullscreen,
                  onPlay: () => {
                    setPlaying(true);
                    showControls();
                  },
                  onPause: () => {
                    setPlaying(false);
                    setControlsVisible(true);
                  },
                  onTimeUpdate: (e) => setTime(e.currentTarget.currentTime),
                  onDurationChange: (e) => setDuration(e.currentTarget.duration || 0),
                  onLoadedMetadata: (e) => {
                    const v = e.currentTarget;
                    setDims({ w: v.videoWidth, h: v.videoHeight });
                    setVolume(v.volume);
                    setMuted(v.muted);
                  },
                  onProgress: (e) => {
                    const v = e.currentTarget;
                    if (v.buffered.length) setBuffered(v.buffered.end(v.buffered.length - 1));
                  },
                  onVolumeChange: (e) => {
                    setVolume(e.currentTarget.volume);
                    setMuted(e.currentTarget.muted);
                  }
                }
              ),
              !playing && /* @__PURE__ */ jsx(
                "button",
                {
                  className: "absolute inset-0 m-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/25 transition-colors",
                  onClick: togglePlay,
                  "aria-label": "Play",
                  children: /* @__PURE__ */ jsx(Icon, { name: "play", className: "ml-1 h-7 w-7" })
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: cn("absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/85 to-transparent px-3 pb-2 pt-8 transition-opacity", !controlsVisible && "opacity-0 pointer-events-none"), children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "group relative mb-2 h-3 cursor-pointer",
                    onClick: onScrub,
                    onMouseMove: onScrubHover,
                    onMouseLeave: () => setHoverTime(null),
                    role: "slider",
                    "aria-label": "Seek",
                    "aria-valuemin": 0,
                    "aria-valuemax": Math.round(duration),
                    "aria-valuenow": Math.round(time),
                    children: [
                      /* @__PURE__ */ jsxs("div", { className: "absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-white/20 group-hover:h-1.5 transition-all", children: [
                        /* @__PURE__ */ jsx("div", { className: "absolute inset-y-0 left-0 rounded-full bg-white/35", style: { width: `${bufferedPct}%` } }),
                        /* @__PURE__ */ jsx("div", { className: "absolute inset-y-0 left-0 rounded-full bg-white", style: { width: `${progress}%` } })
                      ] }),
                      /* @__PURE__ */ jsx("div", { className: "absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity", style: { left: `${progress}%` } }),
                      hoverTime !== null && /* @__PURE__ */ jsx("div", { className: "absolute -top-7 -translate-x-1/2 rounded bg-black/80 px-1.5 py-0.5 text-[11px] text-white", style: { left: `${hoverTime / duration * 100}%` }, children: formatTime(hoverTime) })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                  /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Back 5s (\u2190)", onClick: () => seekBy(-SEEK_SECONDS), children: /* @__PURE__ */ jsx(Icon, { name: "skip-back", className: "h-4 w-4" }) }),
                  /* @__PURE__ */ jsx("button", { className: iconBtn, title: playing ? "Pause (Space)" : "Play (Space)", onClick: togglePlay, "aria-label": playing ? "Pause" : "Play", children: /* @__PURE__ */ jsx(Icon, { name: playing ? "pause" : "play", className: "h-5 w-5" }) }),
                  /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Forward 5s (\u2192)", onClick: () => seekBy(SEEK_SECONDS), children: /* @__PURE__ */ jsx(Icon, { name: "skip-forward", className: "h-4 w-4" }) }),
                  /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Previous frame (,)", onClick: () => stepFrame(-1), children: /* @__PURE__ */ jsx(Icon, { name: "chevron-left", className: "h-4 w-4" }) }),
                  /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Next frame (.)", onClick: () => stepFrame(1), children: /* @__PURE__ */ jsx(Icon, { name: "chevron-right", className: "h-4 w-4" }) }),
                  /* @__PURE__ */ jsxs("span", { className: "ml-2 font-mono text-xs tabular-nums text-white/90", children: [
                    formatTime(time),
                    " / ",
                    formatTime(duration)
                  ] }),
                  /* @__PURE__ */ jsxs("div", { className: "ml-auto flex items-center gap-1", children: [
                    /* @__PURE__ */ jsx("button", { className: iconBtn, title: muted ? "Unmute (M)" : "Mute (M)", onClick: toggleMute, children: /* @__PURE__ */ jsx(Icon, { name: muted || volume === 0 ? "volume-x" : "volume-2", className: "h-4 w-4" }) }),
                    /* @__PURE__ */ jsx(
                      "input",
                      {
                        type: "range",
                        min: 0,
                        max: 1,
                        step: 0.02,
                        value: muted ? 0 : volume,
                        "aria-label": "Volume",
                        className: "h-1 w-20 cursor-pointer accent-white",
                        onChange: (e) => {
                          const v = video();
                          if (!v) return;
                          v.volume = Number(e.target.value);
                          v.muted = v.volume === 0;
                        }
                      }
                    ),
                    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
                      /* @__PURE__ */ jsxs("button", { className: cn(iconBtn, "w-auto px-2 font-mono text-xs"), title: "Playback speed", onClick: () => setSpeedMenuOpen((o) => !o), "aria-haspopup": "menu", "aria-expanded": speedMenuOpen, children: [
                        speed,
                        "\xD7"
                      ] }),
                      speedMenuOpen && /* @__PURE__ */ jsx("div", { role: "menu", className: "absolute bottom-full right-0 mb-1 w-20 rounded-md border border-white/10 bg-black/90 p-1 backdrop-blur", children: SPEEDS.map((r) => /* @__PURE__ */ jsxs("button", { role: "menuitem", className: cn("block w-full rounded px-2 py-1 text-left font-mono text-xs text-white/90 hover:bg-white/15", r === speed && "bg-white/20"), onClick: () => applySpeed(r), children: [
                        r,
                        "\xD7"
                      ] }, r)) })
                    ] }),
                    /* @__PURE__ */ jsx("button", { className: cn(iconBtn, loop && "bg-white/20"), title: "Loop (L)", onClick: toggleLoop, "aria-pressed": loop, children: /* @__PURE__ */ jsx(Icon, { name: "repeat", className: "h-4 w-4" }) }),
                    typeof document !== "undefined" && document.pictureInPictureEnabled && /* @__PURE__ */ jsx("button", { className: iconBtn, title: "Picture in picture (P)", onClick: togglePiP, children: /* @__PURE__ */ jsx(Icon, { name: "picture-in-picture-2", className: "h-4 w-4" }) }),
                    /* @__PURE__ */ jsx("button", { className: iconBtn, title: fullscreen ? "Exit fullscreen (F)" : "Fullscreen (F)", onClick: toggleFullscreen, children: /* @__PURE__ */ jsx(Icon, { name: fullscreen ? "minimize" : "maximize", className: "h-4 w-4" }) })
                  ] })
                ] })
              ] })
            ]
          }
        )
      ]
    }
  ) }) }) });
}

export {
  VideoPlayer
};
//# sourceMappingURL=chunk-WGA24APS.js.map